import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import axiosInstance from "../AxiosInstance";

interface CustomerOrderingDashboardRow {
    customerId: number;
    customerName: string;
    totalOrdersYtd: number;
    lastOrderDateUtc: string;
    orderRegularityDays: number;
    daysSinceLastOrder: number;
    daysOver: number;
    isOverdue: boolean;
}

interface CustomerOrderingDashboardData {
    forecastYearStartDateUtc: string;
    forecastYearEndDateUtc: string;
    rows: CustomerOrderingDashboardRow[];
}

const formatDate = (value?: string): string => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-GB");
};

const CustomerOrderingDashboardPanel: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CustomerOrderingDashboardData | null>(null);
    const gridBackgroundColor = theme.palette.mode === "light" ? "#e0edff40" : theme.palette.action.hover;
    const headerBackgroundColor = theme.palette.mode === "light" ? "#d7e6ff70" : theme.palette.action.selected;

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            try {
                setLoading(true);
                const response = await axiosInstance.get<CustomerOrderingDashboardData>("/dashboard/reorder-status");
                if (!cancelled) {
                    setData(response.data);
                    setError(null);
                }
            } catch (err) {
                console.error("Error loading customer ordering dashboard data:", err);
                if (!cancelled) {
                    setError("Could not load customer reorder data.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const columns: GridColDef<CustomerOrderingDashboardRow>[] = useMemo(() => ([
        {
            field: "customerName",
            headerName: "Customer",
            flex: 1.2,
            minWidth: 220,
        },
        {
            field: "lastOrderDateUtc",
            headerName: "Last Order",
            type: "date",
            minWidth: 170,
            valueGetter: (_, row) => {
                if (!row.lastOrderDateUtc) return null;
                return new Date(row.lastOrderDateUtc);
            },
            valueFormatter: (value) => {
                const dateValue = value as Date | null;
                if (!dateValue || Number.isNaN(dateValue.getTime())) return "";
                return formatDate(dateValue.toISOString());
            },
        },
        {
            field: "totalOrdersYtd",
            headerName: "Total Orders",
            type: "number",
            minWidth: 170,
            headerAlign: "right",
            align: "right",
        },
        {
            field: "orderRegularityDays",
            headerName: "Interval Days",
            type: "number",
            minWidth: 170,
            headerAlign: "right",
            align: "right",
        },
        {
            field: "daysOver",
            headerName: "Days Overdue",
            type: "number",
            minWidth: 190,
            headerAlign: "right",
            align: "right",
        },
    ]), []);

    const subtitle = useMemo(() => {
        if (!data) return "";
        return `YTD from ${formatDate(data.forecastYearStartDateUtc)}`;
    }, [data]);

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, p: "1rem" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography variant="h6" align="left" gutterBottom sx={{ mb: 0 }}>
                    Reorder Status
                </Typography>
                {subtitle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>
            {error ? (
                <Alert severity="error">{error}</Alert>
            ) : loading ? (
                <Skeleton variant="rectangular" width="100%" height={420} />
            ) : (
                <Box sx={{ height: 420, width: "100%" }}>
                    <DataGridPro
                        rows={data?.rows || []}
                        columns={columns}
                        getRowId={(row) => row.customerId}
                        disableRowSelectionOnClick
                        pagination
                        pageSizeOptions={[15, 25, 50]}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "daysOver", sort: "desc" }],
                            },
                            pagination: {
                                paginationModel: { pageSize: 15, page: 0 },
                            },
                        }}
                        sx={{
                            border: "none",
                            borderRadius: 2,
                            backgroundColor: gridBackgroundColor,
                            "& .MuiDataGrid-main": {
                                borderRadius: 2,
                                overflow: "hidden",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: headerBackgroundColor,
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                            },
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: headerBackgroundColor,
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                whiteSpace: "nowrap !important",
                            },
                            "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
                                backgroundColor: headerBackgroundColor,
                            },
                        }}
                    />
                </Box>
            )}
        </Card>
    );
};

export default CustomerOrderingDashboardPanel;
